import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Navigation } from "../../components/Navigation";
import { User } from "lucide-react";
import { useNoindex } from "../../hooks/useNoindex";

// Customer Register Page
export const CustomerRegisterPage = () => {
  useNoindex();
  const { registerCustomer, user } = useAuth();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", password: "", confirm: "",
    referrer_code: "", codice_fiscale: "",
    country: "IT", address: "", city: "", postal_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Country list: IT + EU + popular international destinations
  const COUNTRIES = [
    { code: "IT", name: "Italia", cf_required: true },
    { code: "FR", name: "Francia" }, { code: "DE", name: "Germania" },
    { code: "ES", name: "Spagna" }, { code: "BE", name: "Belgio" },
    { code: "NL", name: "Paesi Bassi" }, { code: "PT", name: "Portogallo" },
    { code: "AT", name: "Austria" }, { code: "PL", name: "Polonia" },
    { code: "GB", name: "Regno Unito" }, { code: "IE", name: "Irlanda" },
    { code: "CH", name: "Svizzera" }, { code: "CZ", name: "Cechia" },
    { code: "HU", name: "Ungheria" }, { code: "RO", name: "Romania" },
    { code: "BG", name: "Bulgaria" }, { code: "GR", name: "Grecia" },
    { code: "DK", name: "Danimarca" }, { code: "SE", name: "Svezia" },
    { code: "FI", name: "Finlandia" }, { code: "NO", name: "Norvegia" },
    { code: "US", name: "Stati Uniti" }, { code: "CA", name: "Canada" },
    { code: "BR", name: "Brasile" }, { code: "JP", name: "Giappone" },
    { code: "OTHER", name: "Altro paese" },
  ];
  const cfRequired = form.country === "IT";

  // Redirect already-logged-in customers to /account
  useEffect(() => {
    document.title = "Registrati | Edicola Cerasuolo";
    if (user && user.role === 'customer') {
      window.location.href = '/account';
    }
  }, [user]);

  // Pre-fill referrer_code from ?ref= query param (deep-link from a friend's
  // share). Runs once on mount — URL search params don't change without nav.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      let ref = (params.get("ref") || "").trim().toUpperCase();
      // Fallback: stashed referrer from a previous social-share visit
      if (!ref) {
        const stored = localStorage.getItem("referrer_code");
        const expires = parseInt(localStorage.getItem("referrer_code_expires") || "0", 10);
        if (stored && expires > Date.now()) ref = stored.toUpperCase();
      }
      if (ref) setForm((prev) => ({ ...prev, referrer_code: ref }));
    } catch (_) { /* no-op */ }
  }, []);

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // All-required field validations
    if (!form.full_name.trim()) { setError("Inserisci nome e cognome"); return; }
    if (!form.email.trim()) { setError("Inserisci email"); return; }
    if (!form.phone.trim() || form.phone.trim().length < 6) {
      setError("Telefono obbligatorio");
      return;
    }
    if (!form.address.trim()) { setError("Indirizzo obbligatorio"); return; }
    if (!form.city.trim()) { setError("Città obbligatoria"); return; }
    if (!form.postal_code.trim()) { setError("CAP obbligatorio"); return; }
    if (cfRequired) {
      const cf = form.codice_fiscale.trim().toUpperCase();
      if (!cf) { setError("Codice fiscale obbligatorio per l'Italia"); return; }
      if (!/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(cf)) {
        setError("Codice fiscale non valido (formato: RSSMRA85M01H501Z)");
        return;
      }
    }
    if (form.password !== form.confirm) {
      setError("Le password non coincidono");
      return;
    }
    if (form.password.length < 6) {
      setError("La password deve avere almeno 6 caratteri");
      return;
    }
    setLoading(true);
    const result = await registerCustomer(
      form.email.trim().toLowerCase(),
      form.password,
      form.full_name.trim(),
      form.phone.trim(),
      form.referrer_code.trim().toUpperCase() || null,
      cfRequired ? form.codice_fiscale.trim().toUpperCase() : (form.codice_fiscale.trim().toUpperCase() || null),
      {
        country: form.country,
        address: form.address.trim(),
        city: form.city.trim(),
        postal_code: form.postal_code.trim(),
      },
    );
    setLoading(false);
    if (result.success) {
      window.location.href = '/account';
    } else {
      setError(result.error || 'Errore di registrazione');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Navigation />
      <div className="max-w-md mx-auto px-4 py-12">
        <div className="bg-[#12121a] border border-gray-800 rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500/10 rounded-full mb-4">
              <User className="w-8 h-8 text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Crea il tuo account</h1>
            <p className="text-gray-500 text-sm">Salva i tuoi dati e velocizza i prossimi acquisti</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-register-form">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Nome e Cognome</label>
              <input
                type="text"
                required
                data-testid="register-name-input"
                value={form.full_name}
                onChange={handleChange('full_name')}
                className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email</label>
              <input
                type="email"
                required
                data-testid="register-email-input"
                value={form.email}
                onChange={handleChange('email')}
                className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Telefono *</label>
              <input
                type="tel"
                required
                data-testid="register-phone-input"
                value={form.phone}
                onChange={handleChange('phone')}
                placeholder="+39 333 1234567"
                className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Paese *</label>
              <select
                required
                data-testid="register-country-select"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
              >
                {COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Indirizzo *</label>
              <input
                type="text"
                required
                data-testid="register-address-input"
                value={form.address}
                onChange={handleChange('address')}
                placeholder="Via Roma, 1"
                className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Città *</label>
                <input
                  type="text"
                  required
                  data-testid="register-city-input"
                  value={form.city}
                  onChange={handleChange('city')}
                  className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">CAP *</label>
                <input
                  type="text"
                  required
                  data-testid="register-postal-input"
                  value={form.postal_code}
                  onChange={handleChange('postal_code')}
                  className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Password (min. 6 caratteri)</label>
              <input
                type="password"
                required
                data-testid="register-password-input"
                value={form.password}
                onChange={handleChange('password')}
                className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Conferma Password</label>
              <input
                type="password"
                required
                data-testid="register-confirm-input"
                value={form.confirm}
                onChange={handleChange('confirm')}
                className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Codice Fiscale {cfRequired ? <span className="text-red-400">*</span> : <span className="text-gray-600 text-xs">(opzionale per estero)</span>}
              </label>
              <input
                type="text"
                required={cfRequired}
                data-testid="register-codice-fiscale-input"
                value={form.codice_fiscale}
                onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase().replace(/\s+/g, "") })}
                placeholder="RSSMRA85M01H501Z"
                maxLength={16}
                className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white font-mono uppercase tracking-wider focus:border-amber-500 focus:outline-none"
                pattern={cfRequired ? "^[A-Z]{6}\\d{2}[A-Z]\\d{2}[A-Z]\\d{3}[A-Z]$" : undefined}
                title="Formato: 16 caratteri alfanumerici (es. RSSMRA85M01H501Z)"
              />
              <p className="text-xs text-gray-500 mt-1">
                {cfRequired
                  ? "Obbligatorio per l'Italia (fatturazione + anti-fraud)."
                  : "Inseriscilo solo se hai un Codice Fiscale italiano."}
              </p>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Codice amico <span className="text-gray-600 text-xs">(opzionale)</span>
              </label>
              <input
                type="text"
                data-testid="register-referrer-input"
                value={form.referrer_code}
                onChange={(e) => setForm({ ...form, referrer_code: e.target.value.toUpperCase() })}
                placeholder="Es. A3F9B2"
                maxLength={12}
                className="w-full px-4 py-3 bg-[#0a0a0f] border border-amber-500/30 rounded-lg text-amber-200 font-mono uppercase tracking-widest focus:border-amber-500 focus:outline-none"
              />
              {form.referrer_code && (
                <p className="text-xs text-amber-400 mt-1">
                  🎁 Stai usando un codice amico: il tuo amico riceverà uno sconto del 10% sul prossimo ordine!
                </p>
              )}
            </div>
            {error && (
              <div data-testid="register-error" className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg p-3">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg transition disabled:opacity-60"
            >
              {loading ? 'Creazione account...' : 'Crea Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              Hai già un account?{' '}
              <a href="/login" data-testid="link-to-login" className="text-amber-400 hover:text-amber-300 font-medium">
                Accedi
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Customer Account Page (Profile + Orders)
